import { useCallback, useState } from 'react';
import {
    Modal,
    ModalVariant,
    Button,
    FormGroup,
    TextInput,
    Form,
    Card,
    CardTitle,
    CardBody,
    Alert,
} from '@patternfly/react-core';

import { CopyIcon } from '@patternfly/react-icons';
import '../customization-page.css';
import { useDispatch, useSelector } from 'react-redux';
import RoiItem from './RoiItem';
import CopyAdvancedRoisModal from './CopyAdvancedRoisModal';
import { Table, Tbody, Td, Thead, Tr } from '@patternfly/react-table';
import { columnIds, columnLangKeys } from './ContourRoiTable';
import { get } from "lodash-es";
import { useTranslation } from 'react-i18next';
import PhysicalPropertiesDropdown from './PhysicalPropertiesDropdown';
import { GlobalContouringRoi, PhysicalProperty, ContouringRoi, isContouringRoi } from '../../../store/contouring/contouring-types';
import { StoreState } from '../../../store/store';
import { contouringSelectors, roiPhysicalPropertyUpdated } from '../../../store/contouring/contouringSlice';
import { fullRoiDetailsHidden, fullRoiDetailsShown } from '../../../store/appStatus/appStatusSlice';
import { CustomizationObjectType } from '../../../store/global-types/customization-types';
import CodingSchemeFields from './CodingSchemeFields';


const FullRoiDetailsModal = () => {
    const dispatch = useDispatch();
    const { t } = useTranslation();

    const fullRoiDetails = useSelector((state: StoreState) => state.appStatus.fullRoiDetails);
    const isGlobalRoi = fullRoiDetails !== undefined && fullRoiDetails.isGlobalRoi === true;
    const validationError = useSelector((state: StoreState) => fullRoiDetails ? contouringSelectors.selectCustomizationValidationError(state, fullRoiDetails.currentRoiId) : undefined);

    const roi = useSelector((state: StoreState): GlobalContouringRoi | ContouringRoi | undefined => {
        if (!fullRoiDetails) {
            return undefined;
        }
        return isGlobalRoi
            ? contouringSelectors.selectGlobalRoiById(state, fullRoiDetails.currentRoiId)
            : contouringSelectors.selectRoiById(state, fullRoiDetails.currentRoiId);
    });

    const handleClose = useCallback(() => {
        dispatch(fullRoiDetailsHidden());
    }, [dispatch]);

    const handlePreviousRoi = useCallback(() => {
        if (fullRoiDetails) {
            const { currentRoiId, roiIdList } = fullRoiDetails;
            const currentRoiListIndex = roiIdList.indexOf(currentRoiId);
            if (currentRoiListIndex > 0) {
                dispatch(fullRoiDetailsShown({ currentRoiId: roiIdList[currentRoiListIndex - 1], roiIdList, isGlobalRoi }));
            }
        }
    }, [dispatch, fullRoiDetails, isGlobalRoi]);

    const handleNextRoi = useCallback(() => {
        if (fullRoiDetails) {
            const { currentRoiId, roiIdList } = fullRoiDetails;
            const currentRoiListIndex = roiIdList.indexOf(currentRoiId);
            if (currentRoiListIndex < roiIdList.length - 1) {
                dispatch(fullRoiDetailsShown({ currentRoiId: roiIdList[currentRoiListIndex + 1], roiIdList, isGlobalRoi }));
            }
        }
    }, [dispatch, fullRoiDetails, isGlobalRoi]);

    const handlePhysicalPropertyValueChanged = useCallback((_: unknown, text: string) => {
        if (roi) {
            dispatch(roiPhysicalPropertyUpdated({ roiId: roi.id, physicalPropertyAttribute: undefined, physicalPropertyValue: text, isGlobalRoi: isGlobalRoi }));
        }
    }, [dispatch, isGlobalRoi, roi]);


    const handlePhysicalPropertyAttributeChanged = useCallback((value: PhysicalProperty) => {
        if (roi) { dispatch(roiPhysicalPropertyUpdated({ roiId: roi.id, physicalPropertyAttribute: value, physicalPropertyValue: undefined, isGlobalRoi: isGlobalRoi })); }
    }, [dispatch, isGlobalRoi, roi]);


    const isOpen = fullRoiDetails !== undefined && roi !== undefined;

    const currentIndex = fullRoiDetails ? fullRoiDetails.roiIdList.indexOf(fullRoiDetails.currentRoiId) : -1;
    const hasNext = fullRoiDetails && fullRoiDetails.roiIdList[fullRoiDetails.roiIdList.length - 1] !== fullRoiDetails.currentRoiId;
    const hasPrevious = fullRoiDetails && fullRoiDetails.roiIdList[0] !== fullRoiDetails.currentRoiId;

    const coveredRoisLength = isGlobalRoi ? get(roi, 'coveredRois', ['']).length : -1;

    const hasPhysicalPropertiesValidationError = validationError && validationError.type === CustomizationObjectType.PhysicalProperties;

    const [isCopyModalOpen, setIsCopyModalOpen] = useState(false);
    const showCopyButton = !isGlobalRoi && roi && !roi.isBuiltInRoi;

    return (
        <>

        <Modal
            className="full-roi-details-modal"
            tabIndex={0}
            isOpen={isOpen}
            onClose={handleClose}
            title={isGlobalRoi ? t('fullRoiDetails.title.global', { count: coveredRoisLength }) : t('fullRoiDetails.title.regular')}
            variant={ModalVariant.large}
            actions={[
                <Button
                    key="prev"
                    variant="secondary"
                    onClick={handlePreviousRoi}
                    isDisabled={!hasPrevious}
                    data-testid="full-roi-details-modal-previous-button"
                >
                    {t('fullRoiDetails.previous')}
                </Button>,
                <Button
                    key="next"
                    variant="secondary"
                    onClick={handleNextRoi}
                    isDisabled={!hasNext}
                    data-testid="full-roi-details-modal-next-button"
                >
                    {t('fullRoiDetails.next')}
                </Button>,
                <span
                    key="index"
                    className="list-index"
                >
                    {currentIndex !== -1 && fullRoiDetails && (<span>{currentIndex + 1}/{fullRoiDetails.roiIdList.length}</span>)}
                </span>,
                ...(showCopyButton ? [
                    <Button
                        key="copy-to"
                        variant="secondary"
                        icon={<CopyIcon />}
                        onClick={() => setIsCopyModalOpen(true)}
                        data-testid="full-roi-details-modal-copy-advanced-rois-button"
                    >
                        {t('customizeRoi.copyAdvancedRois.buttonLabel')}
                    </Button>,
                ] : []),
                <Button
                    key="close"
                    variant="tertiary"
                    onClick={handleClose}
                    data-testid="full-roi-details-modal-close-button"
                >
                    {t('common.close')}
                </Button>,
            ]}
            data-testid="full-roi-details-modal"
        >
            {roi && (
                <div>
                    <Table variant='compact'>
                        <Thead aria-label='Full ROI Details'>
                            <Tr>
                                <Td aria-label='Is modified?'></Td>
                                <Td aria-label='Structure Name'>{t(columnLangKeys[columnIds.name])}</Td>
                                <Td aria-label='Is structure included in structure set?'>{t(columnLangKeys[columnIds.isIncluded])}</Td>
                                <Td aria-label='Color'>{t(columnLangKeys[columnIds.color])}</Td>
                                <Td aria-label='Operation'>{t(columnLangKeys[columnIds.operation])}</Td>
                                <Td aria-label='Interpreted Type'>{t(columnLangKeys[columnIds.interpretedType])}</Td>
                            </Tr>
                        </Thead>
                        <Tbody>
                            <RoiItem key={roi.id} roiId={roi.id} hideIdentificationCodeFields isGlobalRoi={isGlobalRoi} />
                        </Tbody>
                    </Table>

                    <div className="identification-code-table">
                        <Form isHorizontal>
                            <CodingSchemeFields roi={roi} isGlobalRoi={isGlobalRoi} />
                            <Card isFlat className="physical-properties-card-container">
                                <CardTitle>{t('fullRoiDetails.physicalProperties.box.title')}</CardTitle>
                                <CardBody className="physical-properties-card">
                                    <FormGroup
                                        label={t('fullRoiDetails.physicalProperties.box.label')}
                                        fieldId="field-roi-physical-property-attribute">
                                        <PhysicalPropertiesDropdown
                                            id="field-roi-physical-property-attribute"
                                            onSelect={handlePhysicalPropertyAttributeChanged}
                                            value={roi.physicalPropertyAttribute || ''} />
                                    </FormGroup>
                                    <FormGroup
                                        label={t('fullRoiDetails.physicalProperties.box.value')}
                                        fieldId="field-roi-physical-property-value">
                                        <TextInput
                                            type="text"
                                            id="field-roi-physical-property-value"
                                            data-testid="physical-property-value-input"
                                            onChange={handlePhysicalPropertyValueChanged}
                                            value={roi.physicalPropertyValue || ''}
                                        />
                                    </FormGroup>
                                    {hasPhysicalPropertiesValidationError &&
                                        (<div className="roi-item validation-error validation-error-box selectable">
                                            <Alert variant="danger" isInline isPlain isExpandable title={t('common.validationError.message', { error: validationError.message })}>
                                                {validationError.field && (<div>Field: {validationError.field}</div>)}
                                                <div>Error type: {validationError.detail}</div>
                                                <div>Error ctx: {validationError.ctx}</div>
                                            </Alert>
                                        </div>)}
                                </CardBody>

                            </Card>

                        </Form>
                    </div>

                </div>
            )}
        </Modal>
        {isCopyModalOpen && roi && isContouringRoi(roi) && roi.customizationOutputId && (
            <CopyAdvancedRoisModal
                isOpen={isCopyModalOpen}
                onClose={() => setIsCopyModalOpen(false)}
                triggeringRoiId={roi.id}
                sourceOutputId={roi.customizationOutputId}
            />
        )}
        </>
    );
}

export default FullRoiDetailsModal;
